import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Plant, Bird, Butterfly, Drop, Tree, Camera,
  Trophy, Buildings, Sparkle, Plus, UsersThree, MapPin, Books, Notebook,
} from "@phosphor-icons/react";

/**
 * OrgHub — /org/:slug/hub
 *
 * The educational-institution dashboard. Anyone can browse templates and
 * see active projects; teachers/admins/researchers can start new ones.
 */
const TEMPLATE_ICON = {
  Tree, Bird, Butterfly, Drop, Plant, Camera, Trophy,
};

const TIER_LABEL = {
  elementary: "Elementary (K-5)",
  middle_high: "Middle & High (6-12)",
  undergrad_plus: "Undergrad / Grad / Research",
};

export default function OrgHub() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [counts, setCounts] = useState({});
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [gardens, setGardens] = useState([]);
  const [starting, setStarting] = useState(null);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of templates) { g[t.group] = g[t.group] || []; g[t.group].push(t); }
    return g;
  }, [templates]);

  const reload = () => {
    Promise.all([
      http.get(`/orgs/${slug}/hub`).then((r) => r.data),
      http.get("/field-task-templates").then((r) => r.data),
      http.get(`/orgs/${slug}/projects`).then((r) => r.data),
      http.get(`/orgs/${slug}/gardens`).then((r) => r.data),
    ]).then(([hub, tmpl, projs, grds]) => {
      setOrg(hub.org); setCounts(hub.counts);
      setTemplates(tmpl.templates); setProjects(projs.projects);
      setGardens(grds.gardens);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
  };
  useEffect(reload, [slug]);

  const startProject = async (tid, tier) => {
    if (!user) return toast.error("Sign in as a teacher first");
    setStarting(tid);
    try {
      const r = await http.post(`/orgs/${slug}/projects`, {
        template_id: tid, tier,
      });
      toast.success("Project started!");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start");
    } finally { setStarting(null); }
  };

  if (!org) return <main className="min-h-screen pt-32 md:pt-36 px-5 md:px-10 bg-[#F5EBDC]"><div className="max-w-5xl mx-auto">Loading…</div></main>;

  const location = [org.city, org.state, org.country].filter(Boolean).join(" · ");

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="org-hub">
      <div className="max-w-6xl mx-auto">
        <Link to={`/org/${slug}`} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> {org.name}
        </Link>

        {/* Hub Header */}
        <header className="mt-4">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">
            <Sparkle size={12} weight="fill" className="inline mr-1" />
            NaturePulse · Hub Dashboard
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
            Learn · Observe · Grow · Document · Protect · Share
          </h1>
          <div className="mt-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
            {org.short_name || org.name} {location && `· ${location}`}
          </div>
        </header>

        {/* Counters */}
        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="org-hub-counts">
          <StatCard icon={Notebook} label="Active projects" value={counts.active_projects || 0} />
          <StatCard icon={Plant}    label="Gardens"          value={counts.gardens || 0} />
          <StatCard icon={UsersThree} label="Members"        value={counts.members || 0} />
          <StatCard icon={Books}    label="Observations"     value={counts.observations || 0} />
        </section>

        {/* Active projects */}
        <section className="mt-10" data-testid="org-hub-projects">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">Active Projects</div>
              <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">Running now</h2>
            </div>
          </div>
          {projects.length === 0 ? (
            <div className="mt-3 rounded-2xl border-2 border-dashed border-black/10 p-6 text-center text-[#3A3830]">
              No projects yet — pick a template below to start.
            </div>
          ) : (
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map((p) => {
                const g = gardens.find((g) => g.project_id === p.id);
                return (
                  <div key={p.id} className="rounded-2xl border border-black/10 bg-white p-4">
                    <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase text-[#3A3830]">
                      {TIER_LABEL[p.tier]} · {p.template_snapshot?.title}
                    </div>
                    <div className="mt-1 font-serif-display text-lg">{p.class_label || p.template_snapshot?.title}</div>
                    <p className="mt-1 text-sm text-[#3A3830]">{p.template_snapshot?.one_liner}</p>
                    <div className="mt-2 flex items-center gap-3 text-xs text-[#3A3830]">
                      <span>{p.observation_count || 0} obs</span>
                      <span>·</span>
                      <span>{p.template_snapshot?.duration_days} days</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link to={`/org/${slug}/project/${p.id}`} className="rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
                        Open
                      </Link>
                      {g && (
                        <Link to={`/org/${slug}/garden/${g.id}`} className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[#B85F2A] hover:text-[#B85F2A]">
                          Open Garden →
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Template picker */}
        <section className="mt-12" data-testid="org-hub-templates">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">Start a Project</div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">Field-tested templates for every level</h2>
            <p className="mt-2 text-[#3A3830] max-w-2xl">
              Each template ships in 3 difficulty tiers so it works for K-5, high school, and undergrad+research alike.
            </p>
          </div>

          {Object.entries(grouped).map(([group, list]) => (
            <div key={group} className="mt-6">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">{group}</div>
              <div className="mt-2 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((t) => {
                  const Icon = TEMPLATE_ICON[t.icon] || Sparkle;
                  return (
                    <div key={t.id} className="rounded-2xl border border-black/10 bg-white p-4 flex flex-col" data-testid={`template-${t.id}`}>
                      <div className="flex items-center gap-2 text-[#B85F2A]">
                        <Icon size={18} weight="duotone" />
                        <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase">{t.categories.slice(0, 2).join(" · ")}</div>
                      </div>
                      <div className="mt-2 font-serif-display text-lg">{t.title}</div>
                      <p className="mt-1 text-sm text-[#3A3830] flex-1">{t.one_liner}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {["elementary", "middle_high", "undergrad_plus"].map((tier) => (
                          <button
                            key={tier}
                            disabled={starting === t.id}
                            onClick={() => startProject(t.id, tier)}
                            data-testid={`template-start-${t.id}-${tier}`}
                            className="rounded-full border border-black/15 bg-white hover:border-[#B85F2A] hover:text-[#B85F2A] px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.28em] uppercase disabled:opacity-50"
                            title={TIER_LABEL[tier]}
                          >
                            <Plus size={9} weight="bold" className="inline mr-1" />
                            {tier === "elementary" ? "K-5" : tier === "middle_high" ? "6-12" : "Uni+"}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      </div>
    </main>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <div className="flex items-center gap-2 text-[#B85F2A]">
        <Icon size={16} weight="duotone" />
        <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase text-[#3A3830]">{label}</div>
      </div>
      <div className="mt-1 font-serif-display text-3xl">{value}</div>
    </div>
  );
}
